import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../logic/authProvider';
import { useI18n } from '../../../shared/i18n/I18nContext';
import { useTheme } from '../../../shared/theme/ThemeContext';
import LoadingScreen from '../../../shared/ui/LoadingScreen';
import ErrorScreen from '../../../shared/ui/ErrorScreen';
import Routes from '../../../shared/routing/routes';
import './LoginPage.css';


const getAuthErrorMessage = (error, t) => {
  if (!error) {
    return t('genericLoginError');
  }

  const code = error.code;
  const errorMap = {
    INVALID_CREDENTIALS: t('wrongPassword'),
    WRONG_PASSWORD: t('wrongPassword'),
    USER_NOT_FOUND: t('userNotFound'),
    COMPANY_NOT_FOUND: t('companyNotFound'),
    INVALID_TOKEN: t('invalidToken'),
    EXPIRED_TOKEN: t('sessionExpired'),
    TOKEN_EXPIRED: t('sessionExpired'),
    NETWORK_ERROR: t('networkError'),
  };

  return errorMap[code] || error.message || t('genericLoginError');
};

const LoginPage = () => {
  const { t } = useI18n();

  const { theme } = useTheme();
  const { login, clearError, isLoading, error, isAuthenticated } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();
  const formInputStyle =
  {
    border: '1px solid var(--border, rgba(255,255,255,0.06))',
    boxSizing: 'border-box',
    width: '100%',
    padding: '0.75rem 1rem',
    borderRadius: '12px',
    background: 'transparent',
    color: 'inherit',
  };

  const validateForm = () => {
    const errors = {};

    if (!company.trim()) {
      errors.company = t('requiredField');
    }

    if (!email.trim()) {
      errors.email = t('requiredField');
    }

    if (!password.trim()) {
      errors.password = t('requiredField');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      setFormError(t('pleaseFillAllRequiredFields'));
      return;
    }

    setFormError('');
    const user = await login({ company, email, password });
    if (user) {
      navigate(Routes.home);
    }
  };

  if (isAuthenticated) {
    return <Navigate to={Routes.home} replace />;
  }

  if (isLoading) {
    return <LoadingScreen message={t('loading') ?? 'Loading...'} />;
  }

  if (error) {
    return (
      <ErrorScreen
        title={t('error') ?? 'Error'}
        message={getAuthErrorMessage(error, t)}
        onRetry={clearError}
      />
    );
  }

  return (
    <div className="auth-container" style={{ color: theme.text }}>
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="auth-form-header">
              <div className="auth-form-header-left">
                <h3>{t('login')}</h3>
                <p className="form-note">{t('loginPageSubtitle')}</p>
              </div>
            </div>

            <div className="form-row">
              <label className="form-label">{t('company')}</label>
              <input
                type="text"
                style={formInputStyle}
                value={company}
                onChange={(event) => {
                  setCompany(event.target.value);
                  clearError();
                  setFieldErrors((prev) => ({ ...prev, company: '' }));
                }}
                placeholder={t('company')}
              />
              {fieldErrors.company && <div className="input-error">{fieldErrors.company}</div>}
            </div>

            <div className="form-row">
              <label className="form-label">{t('email')}</label>
              <input
                type="text"
                style={formInputStyle}
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  clearError();
                  setFieldErrors((prev) => ({ ...prev, email: '' }));
                }}
                placeholder={t('emailHint')}
              />
              {fieldErrors.email && <div className="input-error">{fieldErrors.email}</div>}
            </div>

            <div className="form-row">
              <label className="form-label">{t('password')}</label>
              <div className="pass-input-div">
                <input
                  style={formInputStyle}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    clearError();
                    setFieldErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  placeholder={t('passwordHint')}
                />
                <button
                  type="button"
                  className="pass-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? (
                    <svg width="35" height="35" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 3l18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M9.88 9.88A3 3 0 0114.12 14.12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  ) : (
                    <svg width="35" height="35" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 12S5.5 5 12 5s9.5 7 9.5 7-3 7-9.5 7S2.5 12 2.5 12z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  )}
                </button>
              </div>
              {fieldErrors.password && <div className="input-error">{fieldErrors.password}</div>}
            </div>

            {formError && <div className="auth-error">{formError}</div>}

            <div className="form-actions">
              <button type="submit" className="auth-button" disabled={isLoading}>
                {isLoading ? t('loading') : t('login')}
              </button>
              <Link className="forgot-link" to={Routes.forgotPassword}>{t('forgotPassword')}</Link>
            </div>
          </form>
      </div>
  );
};

export default LoginPage;
